import React from "react";
import { Search, X } from "lucide-react";
import { useReference } from "@/lib/queries";

const FILTER_LABEL = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5";
const SELECT_CLS =
  "w-full bg-[#050505] border border-[#262626] text-white text-xs px-2.5 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] transition-all appearance-none cursor-pointer hover:border-[#3a3a3a]";
const INPUT_CLS =
  "w-full bg-[#050505] border border-[#262626] text-white text-xs px-2.5 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] transition-all placeholder:text-[#737373]";

export default function FiltersSidebar({ filters, onChange, onReset }) {
  const { data: ref } = useReference();
  const setFilter = (k, v) => onChange({ ...filters, [k]: v });

  const sets = filters.game && ref?.sets_by_game?.[filters.game]
    ? ref.sets_by_game[filters.game]
    : Object.values(ref?.sets_by_game || {}).flat();

  return (
    <aside
      className="border-r border-[#262626] h-full overflow-y-auto bg-[#050505] p-4 flex flex-col gap-4"
      data-testid="filters-sidebar"
    >
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xs uppercase tracking-[0.2em] font-bold text-white">
          Filters
        </h2>
        <button
          onClick={onReset}
          data-testid="reset-filters-btn"
          className="text-[10px] uppercase tracking-wider text-[#737373] hover:text-[#EAB308] flex items-center gap-1 transition-colors"
        >
          <X className="w-3 h-3" />
          Reset
        </button>
      </div>

      <div>
        <div className={FILTER_LABEL}>Search</div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-[#737373]" />
          <input
            data-testid="filter-search-input"
            value={filters.q || ""}
            onChange={(e) => setFilter("q", e.target.value)}
            placeholder="Charizard, ETB..."
            className={`${INPUT_CLS} pl-7`}
          />
        </div>
      </div>

      <div>
        <div className={FILTER_LABEL}>Game</div>
        <select
          data-testid="filter-game"
          value={filters.game || ""}
          onChange={(e) => onChange({ ...filters, game: e.target.value, set_name: "" })}
          className={SELECT_CLS}
        >
          <option value="">All games</option>
          {(ref?.games || []).map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
      </div>

      <div>
        <div className={FILTER_LABEL}>Set <span className="text-[#737373] font-normal normal-case tracking-normal">(or type any)</span></div>
        <input
          data-testid="filter-set"
          list="filter-set-options"
          value={filters.set_name || ""}
          onChange={(e) => setFilter("set_name", e.target.value)}
          className={SELECT_CLS}
          placeholder="All sets"
          autoComplete="off"
        />
        <datalist id="filter-set-options">
          {sets.map((s) => <option key={s} value={s} />)}
        </datalist>
      </div>

      <div>
        <div className={FILTER_LABEL}>Product Type</div>
        <select
          data-testid="filter-product-type"
          value={filters.product_type || ""}
          onChange={(e) => setFilter("product_type", e.target.value)}
          className={SELECT_CLS}
        >
          <option value="">Any product</option>
          {(ref?.product_types || []).map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>

      <div>
        <div className={FILTER_LABEL}>Retailer</div>
        <select
          data-testid="filter-retailer"
          value={filters.retailer_id || ""}
          onChange={(e) => setFilter("retailer_id", e.target.value)}
          className={SELECT_CLS}
        >
          <option value="">All retailers</option>
          {(ref?.retailers || []).map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
      </div>

      <div>
        <div className={FILTER_LABEL}>Region</div>
        <select
          data-testid="filter-region"
          value={filters.region || ""}
          onChange={(e) => setFilter("region", e.target.value)}
          className={SELECT_CLS}
        >
          <option value="">All regions</option>
          {(ref?.regions || []).map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      <div>
        <div className={FILTER_LABEL}>Rarity</div>
        <select
          data-testid="filter-rarity"
          value={filters.rarity || ""}
          onChange={(e) => setFilter("rarity", e.target.value)}
          className={SELECT_CLS}
        >
          <option value="">Any rarity</option>
          {(ref?.rarities || []).map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className={FILTER_LABEL}>Min $</div>
          <input
            data-testid="filter-price-min"
            type="number"
            min="0"
            value={filters.price_min ?? ""}
            onChange={(e) => setFilter("price_min", e.target.value)}
            placeholder="0"
            className={INPUT_CLS}
          />
        </div>
        <div>
          <div className={FILTER_LABEL}>Max $</div>
          <input
            data-testid="filter-price-max"
            type="number"
            min="0"
            value={filters.price_max ?? ""}
            onChange={(e) => setFilter("price_max", e.target.value)}
            placeholder="999"
            className={INPUT_CLS}
          />
        </div>
      </div>

      <div>
        <div className={FILTER_LABEL}>Min Deal Score: {filters.deal_score_min || 0}</div>
        <input
          data-testid="filter-deal-score"
          type="range"
          min="0"
          max="100"
          step="5"
          value={filters.deal_score_min || 0}
          onChange={(e) => setFilter("deal_score_min", e.target.value)}
          className="w-full accent-[#EAB308]"
        />
      </div>

      <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer hover:text-white">
        <input
          data-testid="filter-in-stock"
          type="checkbox"
          checked={filters.in_stock === true}
          onChange={(e) => setFilter("in_stock", e.target.checked ? true : undefined)}
          className="w-3.5 h-3.5 accent-[#EAB308]"
        />
        In stock only
      </label>

      <div>
        <div className={FILTER_LABEL}>Sort</div>
        <select
          data-testid="filter-sort"
          value={filters.sort || "recent"}
          onChange={(e) => setFilter("sort", e.target.value)}
          className={SELECT_CLS}
        >
          <option value="recent">Most recent</option>
          <option value="deal">Best deal</option>
          <option value="price_asc">Price low → high</option>
          <option value="price_desc">Price high → low</option>
        </select>
      </div>
    </aside>
  );
}
